import { HEADERS, REQUESTED_WITH_FETCH, routes } from '@sunucupark/shared/lite';
import type { TwoFactorChallenge, WebLoginRequestInput, WebSessionResponse } from '@sunucupark/shared';
import { AppError } from '../../domain/errors';
import type { SessionRefresher, StoredSession } from '../../application/ports/session';
import type { ApiClient } from '../http/api-client';

/** The contracts arrive with the first call that needs them, not with the page (ADR 0006). */
const webSessionResponse = () =>
  import('@sunucupark/shared').then((contracts) => contracts.WebSessionResponseSchema);
const webLoginResult = () =>
  import('@sunucupark/shared').then((contracts) => contracts.WebLoginResultSchema);
const refreshResponse = () =>
  import('@sunucupark/shared').then((contracts) => contracts.RefreshResponseSchema);

/** Marks a request as coming from our own code; the gateway refuses cookie routes without it. */
export const csrfHeaders: Readonly<Record<string, string>> = {
  [HEADERS.requestedWith]: REQUESTED_WITH_FETCH,
};

/** Turns a browser sign-in or refresh answer into the session the client keeps in memory. */
export function sessionOfWebResponse(response: WebSessionResponse): StoredSession {
  return {
    accountId: response.account.id,
    email: response.account.email,
    accessToken: response.accessToken,
    accessTokenExpiresAt: response.accessTokenExpiresAt,
  };
}

/** A browser sign-in: the session, or the second step it waits for (two-step sign-in). */
export type WebSignInResult = { kind: 'session'; session: StoredSession } | { kind: 'twoFactor'; challenge: TwoFactorChallenge };

/**
 * Browser sign-in, refresh and sign-out (ADR 0007): the refresh token never
 * reaches JavaScript — it travels in the `HttpOnly` cookie the gateway sets, so
 * every call here sends credentials and the CSRF marker.
 */
export class WebAuthApi {
  /** The public client; the code step of a sign-in (`WebTwoFactorApi`) talks through it too. */
  constructor(readonly api: ApiClient) {}

  /** The password: a session, or — for an account with two-step sign-in — the challenge for its code (no cookie yet). */
  async login(credentials: WebLoginRequestInput): Promise<WebSignInResult> {
    const response = await this.api.request({
      method: 'POST',
      path: routes.auth.web.login,
      body: credentials,
      schema: webLoginResult,
      auth: 'none',
      credentials: 'include',
      headers: { ...csrfHeaders },
    });
    return 'twoFactor' in response ? { kind: 'twoFactor', challenge: response.twoFactor } : { kind: 'session', session: sessionOfWebResponse(response) };
  }

  async refresh(): Promise<StoredSession> {
    const response = await this.api.request({
      method: 'POST',
      path: routes.auth.web.refresh,
      schema: webSessionResponse,
      auth: 'none',
      credentials: 'include',
      headers: { ...csrfHeaders },
    });
    return sessionOfWebResponse(response);
  }

  /** Ends the session on the gateway and clears the cookie; safe to call twice. */
  async logout(): Promise<void> {
    await this.api.request({
      method: 'POST',
      path: routes.auth.web.logout,
      auth: 'none',
      credentials: 'include',
      headers: { ...csrfHeaders },
    });
  }
}

/** Renews a browser session with the cookie alone. */
export class WebSessionRefresher implements SessionRefresher {
  constructor(private readonly auth: WebAuthApi) {}

  refresh(): Promise<StoredSession> {
    return this.auth.refresh();
  }
}

/** Renews a session whose refresh token the client holds itself (mobile, desktop). */
export class BearerSessionRefresher implements SessionRefresher {
  constructor(private readonly api: ApiClient) {}

  async refresh(current: StoredSession | null): Promise<StoredSession> {
    if (!current?.refreshToken)
      throw new AppError('REFRESH_TOKEN_INVALID', 'There is no refresh token');
    const response = await this.api.request({
      method: 'POST',
      path: routes.auth.refresh,
      body: { refreshToken: current.refreshToken },
      schema: refreshResponse,
      auth: 'none',
    });
    return {
      accountId: current.accountId,
      email: current.email,
      accessToken: response.tokens.accessToken,
      accessTokenExpiresAt: response.tokens.accessTokenExpiresAt,
      refreshToken: response.tokens.refreshToken,
      refreshTokenExpiresAt: response.tokens.refreshTokenExpiresAt,
    };
  }
}
